/**
 * ResetCategoriesModal - Confirmation modal for resetting category preferences (WP-CAT-11)
 *
 * Follows existing modal pattern from MfaSetupModal.tsx.
 */
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';

export interface ResetCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * Confirmation modal for resetting category preferences to defaults.
 *
 * Pattern source: MfaSetupModal.tsx
 *
 * Accessibility:
 * - role="dialog" and aria-modal="true" for proper screen reader behavior
 * - aria-labelledby links to modal title
 * - Focus trap within modal (via native dialog behavior)
 */
export default function ResetCategoriesModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  error,
}: ResetCategoriesModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-categories-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2
          id="reset-categories-modal-title"
          className="text-lg font-semibold text-gray-900 mb-4"
        >
          Reset Category Preferences
        </h2>

        {error && (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        )}

        <p className="text-sm text-gray-600 mb-4">
          This will reset all your category preferences to the default starter set of approximately 26 commonly used categories.
        </p>

        <p className="text-sm text-gray-600 mb-6">
          Categories that were previously hidden will be reset to their default visibility. This action cannot be undone.
        </p>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onConfirm}
            isLoading={isLoading}
            className="flex-1"
          >
            Reset to Defaults
          </Button>
        </div>
      </div>
    </div>
  );
}
